import React, { useState, useEffect } from 'react';
import { orders as ordersApi } from '../utils/api';
import { fmtDate } from '../utils/helpers';
import { useT } from '../i18n/I18nContext';
import { fobStatus, FOB_META, fobShort, daysBetween } from '../utils/fob';

// ═══════════════════════════════════════════════════════════════════════════
// Prazo de produção e entrega prevista (pedidos)
// ═══════════════════════════════════════════════════════════════════════════
// Entrega prevista = data-base + prazo de produção + transporte/desembaraço.
// A data-base é a do pedido OU a do pagamento do sinal — na prática o
// fornecedor só começa a produzir depois do sinal, e é isso que manda na data.
// A data pode ser sobrescrita à mão quando o fornecedor confirma outra.
export default function DeliveryCard({ quotation: q, onRefresh, toast }) {
  const t = useT();
  const d = q.delivery || {};
  const [form, setForm] = useState({
    order_production_days: q.order_production_days ?? (d.production_days ?? ''),
    production_starts_from: d.starts_from || 'order',
    delivery_transit_days: q.delivery_transit_days ?? (d.transit_days ?? 45),
    estimated_delivery: d.manual || '',
    fob_ready_date: q.fob_ready_date || '',
    fob_ready_actual: q.fob_ready_actual || '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reflete o que voltou do servidor quando o pedido é recarregado
  useEffect(() => {
    const dd = q.delivery || {};
    setForm({
      order_production_days: q.order_production_days ?? (dd.production_days ?? ''),
      production_starts_from: dd.starts_from || 'order',
      delivery_transit_days: q.delivery_transit_days ?? (dd.transit_days ?? 45),
      estimated_delivery: dd.manual || '',
      fob_ready_date: q.fob_ready_date || '',
      fob_ready_actual: q.fob_ready_actual || '',
    });
    setDirty(false);
    // Reage só a troca de pedido / gravação — não a cada render.
  }, [q.id, q.updated_at]); // eslint-disable-line

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); setDirty(true); }

  async function save() {
    setSaving(true);
    try {
      await ordersApi.updateDelivery(q.id, {
        order_production_days: form.order_production_days === '' ? null : Number(form.order_production_days),
        production_starts_from: form.production_starts_from,
        delivery_transit_days: form.delivery_transit_days === '' ? 45 : Number(form.delivery_transit_days),
        estimated_delivery: form.estimated_delivery || '',
        fob_ready_date: form.fob_ready_date || '',
        fob_ready_actual: form.fob_ready_actual || '',
      });
      toast?.(t('delivery.saved'), 'success');
      setDirty(false);
      onRefresh?.();
    } catch (e) {
      toast?.(e.response?.data?.error || t('common.error_saving'), 'error');
    } finally { setSaving(false); }
  }

  // Data-base do modo ESCOLHIDO na tela (o servidor manda as duas), para a
  // prévia acompanhar a troca de "do pedido" ↔ "do sinal" sem novo GET.
  const baseDate = form.production_starts_from === 'deposit'
    ? (d.deposit_date || null)
    : (d.order_date || String(q.order_locked_at || q.created_at || '').slice(0, 10) || null);

  // Fim da produção pelo prazo (referência para negociar a data FOB)
  const byLead = (() => {
    if (!baseDate) return null;
    const dt = new Date(`${baseDate}T12:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    dt.setUTCDate(dt.getUTCDate() + (Number(form.order_production_days) || 0));
    return dt.toISOString().slice(0, 10);
  })();
  // Status da data FOB com o que está na tela (antes de salvar)
  const fob = fobStatus({ ...q, fob_ready_date: form.fob_ready_date, fob_ready_actual: form.fob_ready_actual,
    fob_ready_original: form.fob_ready_date ? (q.fob_ready_original || form.fob_ready_date) : null });
  const fobMeta = FOB_META[fob.status];
  const fobVsLead = form.fob_ready_date && byLead ? daysBetween(byLead, form.fob_ready_date) : null;

  // Prévia local, para o número mexer junto com os campos antes de salvar.
  // Com data FOB acordada, a entrega parte dela (ou da data real de pronto).
  const preview = (() => {
    const fobBase = form.fob_ready_actual || form.fob_ready_date;
    const base = fobBase || baseDate;
    if (!base) return null;
    const days = (fobBase ? 0 : (Number(form.order_production_days) || 0)) + (Number(form.delivery_transit_days) || 0);
    const dt = new Date(`${base}T12:00:00Z`);
    if (Number.isNaN(dt.getTime())) return null;
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
  })();

  const effective = form.estimated_delivery || preview;
  const awaiting = form.production_starts_from === 'deposit' && !d.deposit_date;
  const remaining = effective
    ? Math.round((new Date(`${effective}T12:00:00Z`) - new Date(new Date().toISOString().slice(0, 10) + 'T12:00:00Z')) / 86400000)
    : null;
  const late = remaining != null && remaining < 0 && q.status !== 'Delivered';

  const label = { fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 };
  const input = { width: '100%', padding: '7px 9px', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)', fontSize: 13 };
  const radio = (active) => ({
    flex: 1, padding: '7px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 'var(--radius)',
    border: active ? '2px solid var(--pantone)' : '1px solid var(--gray-200)',
    background: active ? 'var(--pantone-pale)' : 'white',
    color: active ? 'var(--pantone)' : 'var(--gray-600)', fontWeight: active ? 700 : 400,
    textAlign: 'center',
  });

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)' }}>📅 {t('delivery.title')}</div>
        {effective && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 999,
            background: late ? '#FEF2F2' : 'var(--pantone-pale)',
            border: `1px solid ${late ? '#FECACA' : 'transparent'}`,
          }}>
            <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{t('delivery.expected')}</span>
            <strong style={{ fontSize: 14, color: late ? '#B91C1C' : 'var(--pantone)' }}>{fmtDate(effective)}</strong>
            {remaining != null && (
              <span style={{ fontSize: 11, color: late ? '#B91C1C' : 'var(--gray-500)' }}>
                {late ? `· ${Math.abs(remaining)} ${t('delivery.days_late')}` : `· ${remaining} ${t('delivery.days_to_go')}`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Data FOB: compromisso do fornecedor de terminar a produção — é o que
          acompanhamos e cobramos */}
      <div data-testid="fob-box" style={{ border: `1.5px solid ${fob.status === 'late' ? '#FECACA' : 'var(--accent)'}`, background: fob.status === 'late' ? '#FFF7F7' : 'var(--accent-light, #FAF5F6)',
        borderRadius: 'var(--radius)', padding: 12, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <strong style={{ fontSize: 12, color: 'var(--pantone)' }}>🏭 {t('delivery.fob_title')}</strong>
          <span data-testid="fob-status" style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: fobMeta.bg, color: fobMeta.color }}>
            {fobMeta.icon} {fobMeta.label}{fobShort(fob) && fob.status !== 'done' ? ` · ${fobShort(fob)}` : ''}
          </span>
          {fob.slip_days > 0 && (
            <span data-testid="fob-slip" style={{ fontSize: 11, color: '#B45309' }}>
              ⚠ {t('delivery.fob_postponed')} {fob.slip_days} {t('delivery.days_to_go')} ({t('delivery.fob_first')}: {fmtDate(fob.original)})
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
          <div>
            <label style={label}>{t('delivery.fob_date')}</label>
            <input type="date" style={input} aria-label={t('delivery.fob_date')}
              value={form.fob_ready_date} onChange={e => set('fob_ready_date', e.target.value)} />
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>
              {byLead ? `${t('delivery.fob_by_lead')} ${fmtDate(byLead)}` : t('delivery.fob_hint')}
              {fobVsLead != null && fobVsLead !== 0 && (
                <span style={{ color: fobVsLead > 0 ? '#B45309' : '#276749' }}> ({fobVsLead > 0 ? '+' : ''}{fobVsLead} {t('delivery.days_to_go')})</span>
              )}
            </div>
          </div>
          <div>
            <label style={label}>{t('delivery.fob_actual')}</label>
            <input type="date" style={input} aria-label={t('delivery.fob_actual')}
              value={form.fob_ready_actual} onChange={e => set('fob_ready_actual', e.target.value)} />
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>
              {fob.finished_delta != null
                ? (fob.finished_delta > 0 ? `${fob.finished_delta} ${t('delivery.fob_after')}` : fob.finished_delta < 0 ? `${-fob.finished_delta} ${t('delivery.fob_before')}` : t('delivery.fob_on_time'))
                : t('delivery.fob_actual_hint')}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <div>
          <label style={label}>{t('delivery.production_days')}</label>
          <input type="number" min="0" style={input}
            value={form.order_production_days}
            placeholder={String(d.production_days ?? 0)}
            onChange={e => set('order_production_days', e.target.value)} />
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>{t('delivery.production_hint')}</div>
        </div>

        <div>
          <label style={label}>{t('delivery.transit_days')}</label>
          <input type="number" min="0" style={input}
            value={form.delivery_transit_days}
            onChange={e => set('delivery_transit_days', e.target.value)} />
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>{t('delivery.transit_hint')}</div>
        </div>

        <div style={{ gridColumn: 'span 2', minWidth: 260 }}>
          <label style={label}>{t('delivery.counts_from')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={radio(form.production_starts_from === 'order')}
              onClick={() => set('production_starts_from', 'order')}>
              📦 {t('delivery.from_order')}
            </button>
            <button type="button" style={radio(form.production_starts_from === 'deposit')}
              onClick={() => set('production_starts_from', 'deposit')}>
              💰 {t('delivery.from_deposit')}
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>
            {form.production_starts_from === 'deposit'
              ? (awaiting ? `⏳ ${t('delivery.awaiting_deposit')}` : `${t('delivery.base_deposit')} ${fmtDate(d.deposit_date)}`)
              : `${t('delivery.base_order')} ${fmtDate(baseDate)}`}
          </div>
        </div>

        <div>
          <label style={label}>{t('delivery.manual_date')}</label>
          <input type="date" style={input}
            value={form.estimated_delivery}
            onChange={e => set('estimated_delivery', e.target.value)} />
          <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 3 }}>
            {form.estimated_delivery
              ? `${t('delivery.manual_wins')}${preview ? ` (${t('delivery.calculated')}: ${fmtDate(preview)})` : ''}`
              : t('delivery.manual_hint')}
          </div>
        </div>
      </div>

      {/* Linha do tempo do cálculo */}
      {(form.fob_ready_actual || form.fob_ready_date) && preview ? (
        <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--gray-600)' }}>
          🏭 {form.fob_ready_actual ? t('delivery.fob_actual') : t('delivery.fob_date')} {fmtDate(form.fob_ready_actual || form.fob_ready_date)}
          {' → '}<strong>{Number(form.delivery_transit_days) || 0}</strong> {t('delivery.d_transit')}
          {' → '}<strong style={{ color: 'var(--pantone)' }}>{fmtDate(preview)}</strong>
        </div>
      ) : !awaiting && preview && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--gray-50)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--gray-600)' }}>
          {fmtDate(baseDate)}
          {' → '}<strong>{Number(form.order_production_days) || 0}</strong> {t('delivery.d_production')}
          {' → '}<strong>{Number(form.delivery_transit_days) || 0}</strong> {t('delivery.d_transit')}
          {' → '}<strong style={{ color: 'var(--pantone)' }}>{fmtDate(preview)}</strong>
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
        {dirty && <span style={{ fontSize: 11, color: '#B45309' }}>{t('delivery.unsaved')}</span>}
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
          {saving ? '⏳ ...' : `💾 ${t('delivery.save')}`}
        </button>
      </div>
    </div>
  );
}
